import React, { useState, useEffect, useMemo } from 'react';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Box from '@amzn/meridian/box';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AgileUploadRow, { CapacityTuple } from '@components/agile/types/AgileUploadRow';
import { AgileRecommendationInput } from '@components/agile/types/AgileRecommendationTypes';
import { AsinMetadataEntry, Maybe, Uph } from '@graphql/generated/graphql';
import { AgileMacroplanogram, storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import useGetAsinMetadataResults from '@store/hooks/useGetAsinMetadataResult';
import ImageCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ImageCellRenderer';
import QuantityButtonCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/QuantityButtonCellRenderer';
import DeleteButtonCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/DeleteButtonCellRenderer';
import FohCapacityCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/FohCapacityCellRenderer';
import { CellValueChangedEvent } from 'ag-grid-community';
import SubCategoriesCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/SubCategoriesCellRenderer';
import Loader from '@amzn/meridian/loader';
import BuyableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/BuyableCellRenderer';
import TemperatureRatingCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/TemperatureRatingCellRenderer';
import ProcurableCellRenderer from '@components/agile/agileComponents/AtAddNewAsin/AtAddTableGridComponent/ProcurableCellRenderer';
import ColumnSelector from '@components/shared/ColumnSelector';
import { useColumnSelector } from 'src/hooks/useColumnSelector';
import '../../ZoneMap.css';

export interface AtFaceUnitEditTableProps {
  asinList: string[];
  zoneDetails: AgileMacroplanogram | undefined;
  setRecommendationInput: (input: AgileRecommendationInput[]) => void;
  setRecommendationFlag: (RecommendationFlag: boolean) => void;
  setNewAsinMetaData: (asinTitle: AsinMetadataEntry[] | undefined | null) => void;
  setTotalNewFacings: (value: number) => void;
  totalNewFacings: number;
  setInputFlag: (value: boolean) => void;
}

const AtFaceUnitEditTable = (props: AtFaceUnitEditTableProps) => {
  const {
    asinList,
    zoneDetails,
    setRecommendationInput,
    setRecommendationFlag,
    setNewAsinMetaData,
    setTotalNewFacings,
    totalNewFacings,
    setInputFlag,
  } = props;
  const [rowData, setRowData] = useState<AgileUploadRow[]>([]);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [enableLoader, setEnableLoader] = useState(true);
  const [maxFacingFlag, setMaxFacingFlag] = useState(false);
  const getDefaultSubCategoryId = (subCategoryList: storeSubcategory[] | undefined | null, uph?: Maybe<Uph>) => {
    if (subCategoryList?.length === 1) {
      return subCategoryList.at(0)?.subcategoryId;
    }
    const uphl2 = uph?.uphl2 ?? 'Unknown';
    const uphl3 = uph?.uphl3 ?? 'Unknown';

    const matchByUphl2 = subCategoryList?.find((subCategory) =>
      uphl2.toUpperCase().includes(subCategory.subcategoryName.toUpperCase()),
    );
    if (matchByUphl2) {
      return matchByUphl2.subcategoryId;
    }
    const matchByUphl3 = subCategoryList?.find((subCategory) =>
      uphl3.toUpperCase().includes(subCategory.subcategoryName.toUpperCase()),
    );
    return matchByUphl3?.subcategoryId;
  };

  const updateRecommendationInput = (data: AgileUploadRow[]) => {
    const recommendationInput: AgileRecommendationInput[] = [];
    data.forEach((rawRow) => {
      const rowObj = {
        asin: rawRow.asin,
        facingUnits: rawRow.facingUnits,
        subcategoryId: rawRow.subcategoryId,
        fohCapacity: rawRow.fohCapacity.capacity,
      } as AgileRecommendationInput;
      recommendationInput.push(rowObj);
    });
    return recommendationInput;
  };

  const colDefs = [
    {
      headerName: 'ASIN',
      field: 'asin',
      flex: 2,
      minWidth: 120,
      maxWidth: 150,
      filter: true,
      pinned: true,
    },
    {
      headerName: 'Title',
      field: 'title',
      filter: true,
      pinned: true,
    },

    {
      field: 'image',
      cellRenderer: ImageCellRenderer,
      sortable: false,
      pinned: true,
      maxWidth: 100,
    },
    {
      headerName: 'Facings',
      field: 'facingUnits',
      flex: 2,
      minWidth: 180,
      maxWidth: 180,
      pinned: true,
      cellRenderer: QuantityButtonCellRenderer,
    },
    {
      headerName: 'Total FOH Capacity (Units)',
      field: 'fohCapacity',
      flex: 2,
      wrapHeaderText: true,
      minWidth: 130,
      maxWidth: 165,
      headerClass: 'text-center',
      cellRenderer: FohCapacityCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setRecommendationFlag,
        zoneDetails,
      },
      comparator: (a: CapacityTuple, b: CapacityTuple) => Number(a.capacity) - Number(b.capacity),
    },
    {
      headerName: 'SubCategory',
      field: 'subcategoryName',
      editable: false,
      flex: 2,
      minWidth: 170,
      maxWidth: 170,
      cellRenderer: SubCategoriesCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setRecommendationFlag,
      },
    },
    {
      headerName: 'Buyable',
      field: 'buyable',
      cellRenderer: BuyableCellRenderer,
    },
    {
      headerName: 'Procurable',
      field: 'procurable',
      cellRenderer: ProcurableCellRenderer,
    },
    {
      headerName: 'Temp Zone',
      field: 'temperatureRating',
      cellRenderer: TemperatureRatingCellRenderer,
    },
    {
      headerName: 'Shrink Cost (-1W)',
      field: 'shrinkCost',
      filter: true,
      flex: 2,
    },
    {
      headerName: 'Shrink % Ops (-1W) %',
      field: 'shrinkPerOps',
      filter: true,
      minWidth: 160,
      flex: 2,
    },
    {
      headerName: 'Shrink % Ops (-13W) %',
      field: 'shrinkPerOps13week',
      filter: true,
      flex: 2,
      minWidth: 160,
    },
    {
      headerName: 'Shrink % Ops (-26W) %',
      field: 'shrinkPerOps26week',
      filter: true,
      flex: 2,
      minWidth: 160,
    },
    {
      headerName: 'Shrink % Ops (-52W) %',
      field: 'shrinkPerOps52week',
      filter: true,
      flex: 2,
      minWidth: 160,
    },
    {
      headerName: 'Moving Avg Sales (-13W) $',
      field: 'movingAvgSales13week',
      filter: true,
      minWidth: 130,
      flex: 2,
    },
    {
      headerName: 'Moving Avg Sales (-26W) $',
      field: 'movingAvgSales26week',
      filter: true,
      flex: 2,
      minWidth: 160,
    },
    {
      headerName: 'Moving Avg Sales (-52W) $',
      field: 'movingAvgSales52week',
      filter: true,
      flex: 2,
      minWidth: 160,
    },
    {
      headerName: 'UPHL1',
      field: 'uphl1',
      filter: true,
    },
    {
      headerName: 'UPHL2',
      field: 'uphl2',
      filter: true,
    },
    {
      headerName: 'UPHL3',
      field: 'uphl3',
      filter: true,
    },
    {
      headerName: 'Delete',
      cellRenderer: DeleteButtonCellRenderer,
      cellRendererParams: {
        rowData,
        setRowData,
        setDisableButton,
        setTotalNewFacings,
        setMaxFacingFlag,
        setRecommendationFlag,
        setInputFlag,
        zoneDetails,
      },
      sortable: false,
      pinned: true,
    },
  ];

  const defaultVisibleColumns = [
    'asin',
    'title',
    'image',
    'facingUnits',
    'fohCapacity',
    'fohCapacityPerUnit',
    'subcategoryName',
    'buyable',
    'temperatureRating',
    'shrinkPerOps',
    'shrinkPerOps13week',
    'movingAvgSales13week',
    'movingAvgSales52week',
  ];

  const { visibleColumns, columnDefs, toggleColumn } = useColumnSelector({
    allColumns: colDefs,
    defaultVisibleColumns,
    storageKey: 'faceUnitEditColumns',
  });

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      flex: 1,
      resizable: true,
      filter: false,
      minWidth: 100,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    [],
  );

  const response = useGetAsinMetadataResults({
    asinList,
    storeId: zoneDetails?.storeId as string,
  });
  useEffect(() => {
    setRecommendationInput(updateRecommendationInput(rowData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData]);

  useEffect(() => {
    const tempRowData: AgileUploadRow[] = [];
    setEnableLoader(response.asinMetadataList === undefined);
    setNewAsinMetaData(response.asinMetadataList);
    asinList.forEach((rawRow) => {
      const asinMetaData = response.asinMetadataList && response.asinMetadataList?.find((asin) => asin.asin === rawRow);
      const rowObj = {
        asin: rawRow,
        facingUnits: 1,
        image: asinMetaData?.image,
        title: asinMetaData?.title ?? 'Unknown',
        uphl1: asinMetaData?.uph?.uphl1 ?? 'Unknown',
        uphl2: asinMetaData?.uph?.uphl2 ?? 'Unknown',
        uphl3: asinMetaData?.uph?.uphl3 ?? 'Unknown',
        shrinkCost: asinMetaData?.performanceData?.shrinkCost ?? 'N/A',
        shrinkPerOps: asinMetaData?.performanceData?.shrinkPerOps ?? 'N/A',
        shrinkPerOps13week: asinMetaData?.performanceData?.shrinkPerOps13week ?? 'N/A',
        shrinkPerOps26week: asinMetaData?.performanceData?.shrinkPerOps26week ?? 'N/A',
        shrinkPerOps52week: asinMetaData?.performanceData?.shrinkPerOps52week ?? 'N/A',
        movingAvgSales13week: asinMetaData?.performanceData?.movingAvgSales13week ?? 'N/A',
        movingAvgSales26week: asinMetaData?.performanceData?.movingAvgSales26week ?? 'N/A',
        movingAvgSales52week: asinMetaData?.performanceData?.movingAvgSales52week ?? 'N/A',
        subcategoryId: getDefaultSubCategoryId(zoneDetails?.storeSubcategories, asinMetaData?.uph),
        subcategoryName: zoneDetails?.storeSubcategories ?? [],
        fohCapacity: {
          capacity: asinMetaData?.capacityUnitPerFacing?.capacity,
          jumbleFactor: asinMetaData?.capacityUnitPerFacing?.jumbleFactor,
          errorMsg: asinMetaData?.capacityUnitPerFacing?.errorMsg,
        },
        fohCapacityPerUnit: asinMetaData?.capacityUnitPerFacing?.capacity,
        procurable: asinMetaData?.procurable,
        buyable: asinMetaData?.buyable,
        temperatureRating: asinMetaData?.temperatureRating,
      } as AgileUploadRow;
      tempRowData.push(rowObj);
    });
    setRowData(tempRowData);
    const total: number = tempRowData.reduce((t, obj) => t + parseInt(String(obj.facingUnits), 10), 0);
    setTotalNewFacings(total);
    setMaxFacingFlag(total > Number(zoneDetails?.maxFacingUnits));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.asinMetadataList, zoneDetails?.storeSubcategories]);
  const onClick = () => {
    setRecommendationInput(updateRecommendationInput(rowData));
    setRecommendationFlag(true);
  };
  const onCellValueChangedHandler = (event: CellValueChangedEvent<AgileUploadRow>) => {
    if (event.rowIndex != null) {
      const newData = [...rowData];
      // eslint-disable-next-line no-param-reassign
      event.data.fohCapacity.capacity = (event.data.fohCapacityPerUnit as number) * (event.newValue as number);
      const index = newData.findIndex((item) => item.asin === event.data.asin);
      newData[index] = event.data;
      const total: number = newData ? newData.reduce((t, obj) => t + parseInt(String(obj.facingUnits), 10), 0) : 0;
      setTotalNewFacings(total);
      setMaxFacingFlag(total > Number(zoneDetails?.maxFacingUnits));
      setRowData(newData);
    }
  };

  return enableLoader ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <Column spacingInset="400">
      <Row>
        <Column>
          <Text type="h300">Set the number of Facings for your product(s):</Text>
          <Text type="b100">
            Increase or decrease the number of facings for each of the products provided above by clicking + or -
            buttons in the table below
          </Text>
        </Column>
      </Row>
      <Row>
        <Alert type="informational">{`Zone max capacity: ${zoneDetails?.maxFacingUnits} facings`}</Alert>
      </Row>
      <Row>
        <Box type="outline" spacingInset="400">
          <Text type="b200">{`Facings to be added: ${totalNewFacings}`}</Text>
          {maxFacingFlag && (
            <Alert type="error" size="small">
              {`Total number of new facings should be less than or equal to ${zoneDetails?.maxFacingUnits}`}
            </Alert>
          )}
        </Box>
      </Row>
      <Row alignmentHorizontal="end" spacingInset="200">
        <ColumnSelector allColumns={colDefs} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
      </Row>
      <div className="ag-theme-alpine">
        <AgGridReact<AgileUploadRow>
          rowData={rowData}
          domLayout="autoHeight"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          enableCellTextSelection
          onCellValueChanged={onCellValueChangedHandler}
        />
      </div>
      {disableButton && (
        <Alert type="error" size="small">
          Please correct ASINs with defects or missing info.
        </Alert>
      )}
      <Row width="100%" alignmentHorizontal="center" spacingInset="300" backgroundColor="subdued">
        <Button onClick={onClick} disabled={disableButton || maxFacingFlag}>
          Adjust Existing Capacity
        </Button>
      </Row>
    </Column>
  );
};

export default AtFaceUnitEditTable;
